// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Modal from './Modal';
import Button from './Button';
import ShipIcon from './ShipIcon';
import StationIcon from './StationIcon';
import PlanetIcon, { getPlanetColors } from './PlanetIcon';
import WarpGateVisualization from './WarpGateVisualization';
import PlanetExploration from './PlanetExploration';
import UniversalShop from './UniversalShop';
import WeaponShop from './WeaponShop';
import UpgradeShop from './UpgradeShop';
import ModulesShop from './ModulesShop';
import TradeShop from './TradeShop';
import ResourcesShop from './ResourcesShop';
import FuelGauge from './FuelGauge';
import { useFuel } from '../contexts/FuelContext';
import { useCredits } from '../contexts/CreditsContext';
import { useAlerts } from '../contexts/AlertContext';
// import { useModules } from '../contexts/ModulesContext'; // For future cargo display

import GameConfig from '../config/GameConfig';
import MissionManager from '../utils/game/MissionManager';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';
import CrewManager from '../utils/game/CrewManager';
import { calculateCrewCost } from '../config/CrewHiringConfig';
import './DockScreen.css';

/**
 * Unified DockScreen component for both planet orbit and station docking
 * @param {Object} location - The location object (planet or station)
 * @param {Function} onLeave - Callback when leaving orbit/undocking
 * @param {string} type - 'planet', 'station', or 'warpgate'
 * @param {Function} onActivateGate - Callback for activating warp gate (only for type='warpgate')
 */
const DockScreen = ({ location, onLeave, type = 'planet', onActivateGate }) => {
  // Use Context API for fuel and credits
  const { fuel: currentFuel, maxFuel, addFuel, refuelToFull } = useFuel();
  const { credits: currentCredits, removeCredits, addCredits } = useCredits();
  const { success } = useAlerts();
  
  const [showShop, setShowShop] = useState(false);
  const [shopType, setShopType] = useState('weapons');
  const [showWeaponShop, setShowWeaponShop] = useState(false);
  const [showUpgradeShop, setShowUpgradeShop] = useState(false);
  const [showModulesShop, setShowModulesShop] = useState(false);
  const [showTradeShop, setShowTradeShop] = useState(false);
  const [showResourcesShop, setShowResourcesShop] = useState(false);
  const [showRefuelModal, setShowRefuelModal] = useState(false);
  const [showExploration, setShowExploration] = useState(false);
  const [refueling, setRefueling] = useState(false);
  const [refuelMessage, setRefuelMessage] = useState('');
  
  // Note: Old shop modal removed - fuel tank upgrades now in UpgradeShop, weapon reload in WeaponShop
  
  const isStation = type === 'station';

  // Check for mission completion on dock
  useEffect(() => {
    const completedMissions = MissionManager.checkDeliveryCompletion(location.name);
    
    completedMissions.forEach(result => {
      // Award credits
      addCredits(result.rewards.credits);
      
      // Show completion notification
      success(
        `🎉 MISSION COMPLETE!\n${result.mission.title}\n💰 +${result.rewards.credits} Credits | ⭐ +${result.rewards.reputation} Reputation`,
        { duration: 8000 }
      );
      
      // Emit event for other systems
      EventBus.emit(EVENT_TYPES.MISSION_COMPLETE, {
        mission: result.mission,
        credits: result.rewards.credits,
        reputation: result.rewards.reputation
      });
    });
  }, [location.name, addCredits, success]);
  const isWarpGate = type === 'warpgate';
  const fuelNeeded = maxFuel - currentFuel;
  const refuelCost = Math.ceil(fuelNeeded * GameConfig.credits.refuelCostPerPercent);
  
  // Get planet colors (only used for planets, and only if location has required properties)
  const planetColors = !isStation && !isWarpGate && location.name && location.resources && location.threat 
    ? getPlanetColors(location) 
    : { primary: '#4488ff', secondary: '#6699ff', hasRings: false }; // Default colors

  // Generate starfield
  const [stars] = React.useState(() => {
    return Array.from({ length: 150 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 0.5,
      opacity: Math.random() * 0.5 + 0.3,
      twinkleDelay: Math.random() * 3
    }));
  });
  
  // Determine available services from location config
  const services = location.services || [];
  const hasRefuel = services.includes('refuel');
  const hasWeapons = services.includes('weapons');
  const hasUpgrades = services.includes('upgrades');
  const hasTrade = services.includes('trade');
  const hasModules = services.includes('modules');
  const hasExploration = services.includes('exploration');
  const hasCrew = services.includes('crew');
  const hasGateActivation = isWarpGate; // Gates always have activation

  const handleRefuel = () => {
    if (currentFuel >= maxFuel) {
      setRefuelMessage('Fuel tanks already at maximum capacity!');
      setTimeout(() => setRefuelMessage(''), 3000);
      return;
    }

    const fuelNeeded = maxFuel - currentFuel;
    const fullRefuelCost = Math.ceil(fuelNeeded * GameConfig.credits.refuelCostPerPercent);
    
    // Check if we can afford full refuel
    if (currentCredits >= fullRefuelCost) {
      // Full refuel
      setRefueling(true);
      removeCredits(fullRefuelCost, 'full refuel');
      
      setTimeout(() => {
        refuelToFull();
        setRefuelMessage(`✓ Full refuel complete! Cost: ${fullRefuelCost} credits`);
        setRefueling(false);
        setTimeout(() => {
          setRefuelMessage('');
          setShowRefuelModal(false);
        }, 2000);
      }, 1500);
    } else if (currentCredits > 0) {
      // Partial refuel - spend all available credits
      const fuelWeCanAfford = Math.floor(currentCredits / GameConfig.credits.refuelCostPerPercent);
      const actualCost = Math.floor(fuelWeCanAfford * GameConfig.credits.refuelCostPerPercent);
      
      if (fuelWeCanAfford < 1) {
        setRefuelMessage('Insufficient credits! Need at least 1 credit per 1% fuel.');
        setTimeout(() => setRefuelMessage(''), 3000);
        return;
      }
      
      setRefueling(true);
      removeCredits(actualCost, 'partial refuel');
      
      setTimeout(() => {
        addFuel(fuelWeCanAfford);
        setRefuelMessage(`✓ Partial refuel: +${fuelWeCanAfford}% fuel for ${actualCost} credits`);
        setRefueling(false);
        setTimeout(() => {
          setRefuelMessage('');
          setShowRefuelModal(false);
        }, 2000);
      }, 1500);
    } else {
      setRefuelMessage('No credits available for refueling!');
      setTimeout(() => setRefuelMessage(''), 3000);
    }
  };

  return createPortal(
    <div className="dock-screen-overlay">
      {/* Fuel Gauge at Top */}
      <div 
        className="dock-top-fuel-gauge clickable"
        onClick={() => setShowRefuelModal(true)}
        title="Click to refuel"
      >
        <FuelGauge fuelLevel={currentFuel} />
      </div>

      {/* Starfield Background */}
      <div className="dock-starfield">
        {stars.map(star => (
          <div
            key={star.id}
            className="dock-star"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.opacity,
              animation: `twinkle 3s ease-in-out infinite ${star.twinkleDelay}s`
            }}
          />
        ))}
      </div>

      <div className="dock-screen-container">
        {/* Planet/Station/Gate visualization with orbiting/docked ship */}
        <div className="dock-visual-container">
          {isWarpGate ? (
            <>
              {/* Warp Gate Portal Visualization */}
              <div className="warp-gate-display">
                <WarpGateVisualization size={400} discovered={location.discovered !== false} showTractorBeam={true} />
              </div>
              {/* Ship hovering near gate */}
              <div className="gate-ship">
                <ShipIcon size={50} rotation={0} />
              </div>
            </>
          ) : isStation ? (
            <>
              <StationIcon size={400} color="#00ffff" animated={true} />
              {/* Docked ship */}
              <div className="docked-ship">
                <ShipIcon size={50} rotation={0} />
                {/* Docking clamps */}
                <div className="docking-clamp clamp-left"></div>
                <div className="docking-clamp clamp-right"></div>
              </div>
            </>
          ) : (
            <>
              <div 
                className="planet-clickable"
                onClick={() => setShowExploration(true)}
                title="Click to explore planet"
              >
                <PlanetIcon 
                  size={400} 
                  primaryColor={planetColors.primary}
                  secondaryColor={planetColors.secondary}
                  hasAtmosphere={true}
                  hasRings={planetColors.hasRings}
                />
              </div>
              {/* Orbiting ship */}
              <div className="orbiting-ship">
                <ShipIcon size={40} rotation={90} />
              </div>
            </>
          )}
        </div>

        {/* Details panel on right */}
        <div className="dock-details-panel">
          <div className="dock-header">
            <div className="dock-title">{isStation ? 'DOCKED AT' : 'IN ORBIT'}</div>
            <div className="dock-location">{location.name}</div>
            <div className="dock-credits">
              <span className="credits-icon">💰</span>
              <span className="credits-amount">{currentCredits.toLocaleString()}</span>
              <span className="credits-label">CREDITS</span>
            </div>
          </div>

          <div className="dock-info">
            <div className="dock-info-grid">
              <div className="info-card">
                <div className="info-icon">📍</div>
                <div className="info-content">
                  <div className="info-label">DISTANCE</div>
                  <div className="info-value">{location.distance} LY</div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon">{isStation ? '🏢' : '⚠️'}</div>
                <div className="info-content">
                  <div className="info-label">{isStation ? 'TYPE' : 'THREAT'}</div>
                  <div className={`info-value ${!isStation && location.threat ? `threat-${location.threat.toLowerCase()}` : ''}`}>
                    {isStation ? 'STATION' : (location.threat || 'UNKNOWN')}
                  </div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon">{isStation ? '🔧' : '🌐'}</div>
                <div className="info-content">
                  <div className="info-label">{isStation ? 'SERVICES' : 'RESOURCES'}</div>
                  <div className="info-value small">{location.resources}</div>
                </div>
              </div>

              {isStation ? (
                <div className="info-card">
                  <div className="info-icon">🚪</div>
                  <div className="info-content">
                    <div className="info-label">DOCKING BAY</div>
                    <div className="info-value">ALPHA-7</div>
                  </div>
                </div>
              ) : (
                <div className="info-card">
                  <div className="info-icon">🛰️</div>
                  <div className="info-content">
                    <div className="info-label">ORBIT</div>
                    <div className="info-value small">400 KM • 92 MIN</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Services Section - Two Column Grid */}
          <div className="dock-services">
            <div className="services-header">⚡ {isWarpGate ? 'GATE' : (isStation ? 'STATION' : 'ORBITAL')} SERVICES</div>
            
            <div className="services-grid">
              {/* Warp Gate Activation Button - Show only if gate activation available */}
              {hasGateActivation && onActivateGate && (
                <Button 
                  variant="secondary"
                  size="medium"
                  icon="🌀"
                  onClick={() => onActivateGate(location)}
                  style={{
                    gridColumn: '1 / -1' // Span full width
                  }}
                >
                  ACTIVATE GATE
                </Button>
              )}

              {/* Planet Exploration Button - Show if exploration service available */}
              {hasExploration && (
                <Button 
                  variant="success"
                  size="medium"
                  icon="🌍"
                  onClick={() => setShowExploration(true)}
                >
                  EXPLORE
                </Button>
              )}

              {/* Refuel Button - Show if refuel service available */}
              {hasRefuel && (
                <Button 
                  variant="warning"
                  size="medium"
                  icon="⛽"
                  onClick={() => setShowRefuelModal(true)}
                >
                  REFUEL
                </Button>
              )}

              {/* Weapons Shop Button - Show if weapons service available */}
              {hasWeapons && (
                <Button 
                  variant="danger"
                  size="medium"
                  icon="⚔️"
                  onClick={() => setShowWeaponShop(true)}
                >
                  WEAPONS
                </Button>
              )}

              {/* Upgrade Shop Button - Show if upgrades service available */}
              {hasUpgrades && (
                <Button 
                  variant="primary"
                  size="medium"
                  icon="🔧"
                  onClick={() => setShowUpgradeShop(true)}
                >
                  UPGRADES
                </Button>
              )}

              {/* Resources Button - Show if trade service available */}
              {hasTrade && (
                <Button 
                  variant="success"
                  size="medium"
                  icon="🌾"
                  onClick={() => setShowResourcesShop(true)}
                >
                  RESOURCES
                </Button>
              )}

              {/* Trade Goods Button - Show if trade service available */}
              {hasTrade && (
                <Button 
                  variant="success"
                  size="medium"
                  icon="📦"
                  onClick={() => setShowTradeShop(true)}
                >
                  TRADE
                </Button>
              )}

              {/* Ship Modules Button - Show if modules service available */}
              {hasModules && (
                <Button 
                  variant="primary"
                  size="medium"
                  icon="⚙️"
                  onClick={() => setShowModulesShop(true)}
                >
                  MODULES
                </Button>
              )}

              {/* Hire Crew Button - Show if crew service available */}
              {hasCrew && (
                <Button 
                  variant="primary"
                  size="medium"
                  icon="👥"
                  onClick={() => { setShopType('crew'); setShowShop(true); }}
                >
                  HIRE CREW
                </Button>
              )}
            </div>
          </div>

          <Button 
            variant="secondary"
            size="large"
            onClick={onLeave}
          >
            ← {isWarpGate ? 'LEAVE GATE' : (isStation ? 'UNDOCK & DEPART' : 'LEAVE ORBIT')}
          </Button>

          <div className="dock-status">
            <div className="status-indicator"></div>
            <span>{isWarpGate ? 'GATE PROXIMITY MAINTAINED • QUANTUM FIELD STABLE' : (isStation ? 'DOCKING CLAMPS ENGAGED • LIFE SUPPORT ACTIVE' : 'STABLE ORBIT MAINTAINED • ALL SYSTEMS NOMINAL')}</span>
          </div>
        </div>
      </div>

      {/* Weapon Shop Modal */}
      {showWeaponShop && (
        <WeaponShop 
          onClose={() => setShowWeaponShop(false)}
          locationName={location?.name}
        />
      )}

      {/* Upgrade Shop Modal */}
      {showUpgradeShop && (
        <UpgradeShop 
          isOpen={showUpgradeShop}
          onClose={() => setShowUpgradeShop(false)}
          location={location}
        />
      )}

      {/* Modules Shop Modal */}
      {showModulesShop && (
        <ModulesShop 
          isOpen={showModulesShop}
          onClose={() => setShowModulesShop(false)}
          location={location}
        />
      )}

      {/* Trade Shop Modal */}
      {showTradeShop && (
        <TradeShop 
          isOpen={showTradeShop}
          onClose={() => setShowTradeShop(false)}
          location={location}
        />
      )}

      {/* Resources Shop Modal */}
      {showResourcesShop && (
        <ResourcesShop 
          isOpen={showResourcesShop}
          onClose={() => setShowResourcesShop(false)}
          location={location}
        />
      )}

      {/* Universal Shop Modal - Only for crew now */}
      {showShop && shopType === 'crew' && (
        <UniversalShop
          type={shopType}
          onClose={() => {
            setShowShop(false);
          }}
          location={location}
          onPurchase={(item, qty = 1, experienceLevel) => {
            const totalCost = calculateCrewCost(item.basePrice, experienceLevel);
            
            if (currentCredits < totalCost) {
              success(`Insufficient credits! Need ${totalCost} credits.`);
              return;
            }

            const result = CrewManager.hireCrew(item.roleData, experienceLevel, false);
            if (result.success) {
              removeCredits(totalCost);
              success(`✅ ${result.message}\nCost: ${totalCost} credits`);
            } else {
              success(`❌ ${result.message}`);
            }
          }}
        />
      )}

      {/* Planet Exploration Modal - Only for planets */}
      {!isStation && (
        <PlanetExploration
          isOpen={showExploration}
          onClose={() => setShowExploration(false)}
          planetData={location}
          onExplorationComplete={(result) => {
          }}
        />
      )}

      {/* Refuel Modal */}
      {showRefuelModal && (
        <Modal
          isOpen={showRefuelModal}
          onClose={() => !refueling && setShowRefuelModal(false)}
          title="⛽ ANTIMATTER REFUEL SERVICE"
          size="medium"
          className="refuel-modal"
          closeOnBackdrop={!refueling}
        >
          <div className="refuel-modal-content">
            <div className="refuel-description">
              {currentCredits >= refuelCost 
                ? `Complete refueling to ${maxFuel}% capacity using ${isStation ? 'station' : 'orbital'} reserves`
                : currentCredits > 0
                ? `Partial refueling available - Get ${Math.floor(currentCredits / GameConfig.credits.refuelCostPerPercent)}% fuel with available credits`
                : 'Insufficient credits for refueling service'}
            </div>

            <div className="refuel-stats">
              <div className="refuel-stat">
                <span className="refuel-stat-label">Current Fuel:</span>
                <span className="refuel-stat-value fuel">{currentFuel.toFixed(1)}%</span>
              </div>
              <div className="refuel-stat">
                <span className="refuel-stat-label">Max Capacity:</span>
                <span className="refuel-stat-value">{maxFuel}%</span>
              </div>
              <div className="refuel-stat">
                <span className="refuel-stat-label">Fuel Needed:</span>
                <span className="refuel-stat-value">{(maxFuel - currentFuel).toFixed(1)}%</span>
              </div>
              <div className="refuel-stat">
                <span className="refuel-stat-label">Service Cost:</span>
                <span className="refuel-stat-value cost">{refuelCost} credits</span>
              </div>
              <div className="refuel-stat">
                <span className="refuel-stat-label">Your Credits:</span>
                <span className="refuel-stat-value gain">{currentCredits}</span>
              </div>
            </div>

            {refuelMessage && (
              <div className={`refuel-message ${refuelMessage.includes('Insufficient') || refuelMessage.includes('already') ? 'error' : 'success'}`}>
                {refuelMessage}
              </div>
            )}

            <Button 
              variant={currentCredits >= refuelCost ? "success" : "warning"}
              size="large"
              onClick={handleRefuel}
              disabled={refueling || currentFuel >= maxFuel || currentCredits === 0}
              loading={refueling}
            >
              {refueling ? 'REFUELING...' : 
               currentCredits >= refuelCost ? 'REFUEL TO MAX' : 
               currentCredits > 0 ? `PARTIAL REFUEL (${Math.floor(currentCredits / GameConfig.credits.refuelCostPerPercent)}%)` : 
               'INSUFFICIENT CREDITS'}
            </Button>
          </div>
        </Modal>
      )}
    </div>,
    document.body
  );
};

export default DockScreen;
